/* eslint-disable */
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  memo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Menu,
  Breadcrumbs,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useSelector } from "react-redux";

// Icons
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorageIcon from "@mui/icons-material/Storage";
import ArchiveIcon from "@mui/icons-material/Archive";
import { safeFetch } from "./Safefetch";

// API base
const API_BASE =
  "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com" || "";
  //"http://127.0.0.1:8000/";

// Helpers
const DRIVE_LABEL = "Drive";

const formatSize = (bytes) => {
  if (bytes === 0) return "0 B";
  if (!bytes && bytes !== 0) return "—";
  const k = 1024;
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${u[i]}`;
};

const extToType = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (!ext) return "other";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["xls", "xlsx"].includes(ext)) return "xlsx";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  return "other";
};

const iconForType = (type) => {
  switch (type) {
    case "folder":
      return <FolderIcon sx={{ color: "#5f6368" }} />;
    case "pdf":
      return <PictureAsPdfIcon sx={{ color: "#d93025" }} />;
    case "xlsx":
      return <TableChartIcon sx={{ color: "#188038" }} />;
    default:
      return <DescriptionIcon sx={{ color: "#5f6368" }} />;
  }
};

/* ========= SectionCard ========= */
const SectionCard = memo(function SectionCard({ title, children, action }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 2.5,
        mb: 3,
        bgcolor: "#ffffff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2933" }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
});

/* ========= FileTable ========= */
function FileTable({
  title,
  rows,
  showNewButtons = false,
  onCreateFolder,
  onTriggerUpload,
  onMoveToTrash,
  onRestore,
  onDeleteForever,
  onArchive,
  onRowClick,
  onGenerateSummary,
  // ✅ NEW: notify handler from parent
  onNotifyDocument,
}) {
  const [selected, setSelected] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const sorted = useMemo(() => {
      const arr = [...rows];
      arr.sort((a, b) => {
        let A, B;
        switch (sortBy) {
          case "name":
            A =
              (a.type === "folder" ? "0-" : "1-") +
              (a.name || "").toLowerCase();
            B =
              (b.type === "folder" ? "0-" : "1-") +
              (b.name || "").toLowerCase();
            break;
          case "path":
            A = (a.path || "").toLowerCase();
            B = (b.path || "").toLowerCase();
            break;
          case "modified":
            A = Date.parse(a.modified || 0);
            B = Date.parse(b.modified || 0);
            break;
          case "size":
            A = a.size ?? 0;
            B = b.size ?? 0;
            break;
          case "treeType":
            A = (a.treeType || "").toLowerCase();
            B = (b.treeType || "").toLowerCase();
            break;
          default:
            A = (a.name || "").toLowerCase();
            B = (b.name || "").toLowerCase();
        }
        if (A < B) return sortDir === "asc" ? -1 : 1;
        if (A > B) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
      return arr;
    }, [rows, sortBy, sortDir]);
  
  const toggleAll = (checked) =>
    setSelected(checked ? sorted.map((r) => r.id) : []);
  const toggleOne = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );

  const onHeaderClick = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) =>
    sortBy === field ? (
      <ArrowUpwardIcon
        fontSize="small"
        sx={{
          transform: sortDir === "desc" ? "rotate(180deg)" : "none",
          color: "#5f6368",
        }}
      />
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#f9fafb",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500, color: "#111827" }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {showNewButtons && (
            <>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={onTriggerUpload}
                sx={{
                  textTransform: "none",
                  mr: 1,
                  bgcolor: "#2563eb",
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderIcon />}
                onClick={onCreateFolder}
                sx={{ textTransform: "none" }}
              >
                New Folder
              </Button>
            </>
          )}

          {onMoveToTrash && (
            <Button
              sx={{ ml: 1 }}
              startIcon={<DeleteIcon />}
              disabled={selected.length === 0}
              onClick={() => {
                onMoveToTrash(selected);
                setSelected([]);
              }}
            >
              Move to Trash
            </Button>
          )}

          {onArchive && (
            <Button
              sx={{ ml: 1 }}
              startIcon={<ArchiveIcon />}
              disabled={selected.length === 0}
              onClick={() => {
                onArchive(selected);
                setSelected([]);
              }}
            >
              Import
            </Button>
          )}

          {onRestore && (
            <Button
              sx={{ ml: 1 }}
              startIcon={<RestoreFromTrashIcon />}
              disabled={selected.length === 0}
              onClick={() => {
                onRestore(selected);
                setSelected([]);
              }}
            >
              Restore
            </Button>
          )}

          {onDeleteForever && (
            <Button
              sx={{ ml: 1 }}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={selected.length === 0}
              onClick={() => {
                onDeleteForever(selected);
                setSelected([]);
              }}
            >
              Delete forever
            </Button>
          )}

          <IconButton sx={{ ml: 1 }}>
            <SearchIcon />
          </IconButton>
          <IconButton>
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          flex: 1,
          maxHeight: "calc(100vh - 170px)",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#d1d5db",
            borderRadius: 3,
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < sorted.length
                  }
                  checked={sorted.length > 0 && selected.length === sorted.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>

              <TableCell
                onClick={() => onHeaderClick("name")}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Name <SortIcon field="name" />
                </Box>
              </TableCell>

              <TableCell
                onClick={() => onHeaderClick("path")}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Path <SortIcon field="path" />
                </Box>
              </TableCell>

              <TableCell
                onClick={() => onHeaderClick("treeType")}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Type <SortIcon field="treeType" />
                </Box>
              </TableCell>

              <TableCell
                onClick={() => onHeaderClick("modified")}
                sx={{ cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Date modified <SortIcon field="modified" />
                </Box>
              </TableCell>

              <TableCell
                onClick={() => onHeaderClick("size")}
                align="right"
                sx={{ cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  File size <SortIcon field="size" />
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((file) => {
              const checked = selected.includes(file.id);
              return (
                <TableRow
                  key={file.id}
                  hover
                  selected={checked}
                  onClick={() => {
                    if (file.is_folder && onRowClick) onRowClick(file);
                  }}
                  sx={{
                    cursor: file.is_folder ? "pointer" : "default",
                    "&:hover": { backgroundColor: "#f9fafb" },
                    "&.Mui-selected": { backgroundColor: "#e0ecff" },
                    transition: "background-color 0.12s ease",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleOne(file.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>

                  <TableCell sx={{ width: "38%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {iconForType(file.type)}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#111827" }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ color: "#6b7280" }}>
                    {file.path || "—"}
                  </TableCell>
                  <TableCell sx={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                    {file.treeType || "—"}
                  </TableCell>
                  <TableCell sx={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                    {file.modified
                      ? new Date(file.modified).toLocaleString()
                      : "—"}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ color: "#6b7280", whiteSpace: "nowrap" }}
                  >
                    {file.type === "folder" ? "—" : formatSize(file.size)}
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {!file.is_folder && onGenerateSummary && (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, textTransform: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onGenerateSummary(file.id);
                        }}
                      >
                        Summary
                      </Button>
                    )}

                    {/* ✅ Notify button per document */}
                    {!file.is_folder && onNotifyDocument && (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, textTransform: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotifyDocument(file.id);
                        }}
                      >
                        Notify
                      </Button>
                    )}
  
                    <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#6b7280" }}>
                  No items
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/* ========= ManageDatabase ========= */
function ManageDatabase({ showSnackbar }) {
  const [tab, setTab] = useState("states");

  const [states, setStates] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [domains, setDomains] = useState([]);
  const [categories, setCategories] = useState([]);
  const token = localStorage.getItem("labour_law_token");
  const headers = {
    "Content-Type": "application/json",
  };
  const [summarySmallOpen, setSummarySmallOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [stateForm, setStateForm] = useState({ code: "", name: "" });
  const [domainForm, setDomainForm] = useState({ code: "", name: "" });
  const [categoryForm, setCategoryForm] = useState({ code: "", name: "" });
  const [jurForm, setJurForm] = useState({ scope: "CENTRAL", state_id: "" });

  const RESOURCE_PATHS = {
    states: "docmgmt/states",
    domains: "docmgmt/domains",
    categories: "docmgmt/categories",
    jurisdictions: "docmgmt/jurisdictions",
  };

  const apiUrl = (resource, id) =>
    `${API_BASE}/${RESOURCE_PATHS[resource]}/${id ? `${id}/` : ""}`;

  const apiGet = async (resource) => {
    const res = await safeFetch(apiUrl(resource), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET ${resource} failed`);
    return res.json();
  };
  

  const apiPost = async (resource, body) => {
    const res = await safeFetch(apiUrl(resource), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    const txt = await res.text().catch(() => "");
    if (!res.ok) throw new Error(txt || `POST ${resource} failed`);
    return txt ? JSON.parse(txt) : {};
  };

  const apiDelete = async (resource, id) => {
    const res = await safeFetch(apiUrl(resource, id), {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const txt = await res.text().catch(() => "");
    if (!res.ok) throw new Error(txt || `DELETE ${resource}/${id} failed`);
    return true;
  };

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j, d, c] = await Promise.all([
        apiGet("states"),
        apiGet("jurisdictions"),
        apiGet("domains"),
        apiGet("categories"),
      ]);
      setStates(s || []);
      setJurisdictions(j || []);
      setDomains(d || []);
      setCategories(c || []);
    } catch (e) {
      showSnackbar?.(e.message || "Failed to load database data", "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!API_BASE) return;
    refreshAll();
  }, [refreshAll]);

  const createState = async () => {
    const code = (stateForm.code || "").trim().toUpperCase();
    const name = (stateForm.name || "").trim();
    if (!code || !name)
      return showSnackbar("State code and name are required", "error");

    try {
      const created = await apiPost("states", { code, name });
      setStates((prev) => [created, ...prev]);
      setStateForm({ code: "", name: "" });
      showSnackbar("State created");
    } catch (e) {
      showSnackbar(
        e.message.includes("exists") ? e.message : "Failed to create state",
        "error"
      );
    }
  };

  const createDomain = async () => {
    const code = (domainForm.code || "").trim().toUpperCase();
    const name = (domainForm.name || "").trim();
    if (!code || !name)
      return showSnackbar("Domain code and name are required", "error");

    try {
      const created = await apiPost("domains", { code, name });
      setDomains((prev) => [created, ...prev]);
      setDomainForm({ code: "", name: "" });
      showSnackbar("Domain created");
    } catch (e) {
      showSnackbar(
        e.message.includes("exists") ? e.message : "Failed to create domain",
        "error"
      );
    }
  };

  const createCategory = async () => {
    const code = (categoryForm.code || "").trim().toUpperCase();
    const name = (categoryForm.name || "").trim();
    if (!code || !name)
      return showSnackbar("Category code and name are required", "error");

    try {
      const created = await apiPost("categories", { code, name });
      setCategories((prev) => [created, ...prev]);
      setCategoryForm({ code: "", name: "" });
      showSnackbar("Category created");
    } catch (e) {
      showSnackbar(
        e.message.includes("exists") ? e.message : "Failed to create category",
        "error"
      );
    }
  };

  const createJurisdiction = async () => {
    try {
      const created = await apiPost("jurisdictions", { scope: jurForm.scope });
      setJurisdictions((prev) => [created, ...prev]);
      setJurForm({ scope: "CENTRAL", state_id: "" });
      showSnackbar("Jurisdiction created");
    } catch (e) {
      showSnackbar("Failed to create jurisdiction", "error");
    }
  };

  const remove = async (resource, id, after) => {
    try {
      await apiDelete(resource, id);
      after?.();
      showSnackbar("Deleted");
    } catch (e) {
      showSnackbar(e.message || "Failed to delete", "error");
    }
  };

  return (
    <Box
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#f3f4f6",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827" }}>
          Manage Database
        </Typography>
        <Button
          onClick={refreshAll}
          startIcon={<RefreshIconFallback />}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, bgcolor: "#ffffff", borderRadius: 1, minHeight: 40 }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab value="jurisdictions" label="Jurisdictions" />
        <Tab value="states" label="States" />
        <Tab value="domains" label="Domains" />
        <Tab value="categories" label="Categories" />
      </Tabs>

      {tab === "jurisdictions" && (
        <>
          <SectionCard title="Add Jurisdiction">
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="scope-label">Scope</InputLabel>
                <Select
                  labelId="scope-label"
                  label="Scope"
                  value={jurForm.scope}
                  onChange={(e) =>
                    setJurForm((j) => ({ ...j, scope: e.target.value }))
                  }
                >
                  <MenuItem value="CENTRAL">Central</MenuItem>
                  <MenuItem value="STATE">State</MenuItem>
                  <MenuItem value="CENTRAL_WITH_STATE">
                    Central with State
                  </MenuItem>
                </Select>
              </FormControl>

              <Button variant="contained" onClick={createJurisdiction}>
                Create
              </Button>
            </Box>
          </SectionCard>

          <SectionCard title="Jurisdictions">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right" width="120">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {jurisdictions.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell>{j.scope}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="error"
                        size="small"
                        onClick={() =>
                          remove("jurisdictions", j.id, () =>
                            setJurisdictions((prev) =>
                              prev.filter((x) => x.id !== j.id)
                            )
                          )
                        }
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {jurisdictions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No jurisdictions
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SectionCard>
        </>
      )}

      {tab === "states" && (
        <>
          <SectionCard title="Add State" action={null}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Code"
                value={stateForm.code}
                onChange={(e) =>
                  setStateForm((s) => ({ ...s, code: e.target.value }))
                }
                size="small"
              />
              <TextField
                label="Name"
                value={stateForm.name}
                onChange={(e) =>
                  setStateForm((s) => ({ ...s, name: e.target.value }))
                }
                size="small"
                sx={{ minWidth: 260 }}
              />
              <Button variant="contained" onClick={createState}>
                Create
              </Button>
            </Box>
          </SectionCard>

          <SectionCard title="States">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" width="120">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {states.map((s) => (
                  <TableRow key={s.id || s.code}>
                    <TableCell>{s.code}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="error"
                        size="small"
                        onClick={() =>
                          remove("states", s.id, () =>
                            setStates((prev) =>
                              prev.filter((x) => x.id !== s.id)
                            )
                          )
                        }
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {states.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No states
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SectionCard>
        </>
      )}

      {tab === "domains" && (
        <>
          <SectionCard title="Add Domain">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Code"
                value={domainForm.code}
                onChange={(e) =>
                  setDomainForm((s) => ({ ...s, code: e.target.value }))
                }
                size="small"
              />
              <TextField
                label="Name"
                value={domainForm.name}
                onChange={(e) =>
                  setDomainForm((s) => ({ ...s, name: e.target.value }))
                }
                size="small"
                sx={{ minWidth: 260 }}
              />
              <Button variant="contained" onClick={createDomain}>
                Create
              </Button>
            </Box>
          </SectionCard>

          <SectionCard title="Domains">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" width="120">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.map((d) => (
                  <TableRow key={d.id || d.code}>
                    <TableCell>{d.code}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="error"
                        size="small"
                        onClick={() =>
                          remove("domains", d.id, () =>
                            setDomains((prev) =>
                              prev.filter((x) => x.id !== d.id)
                            )
                          )
                        }
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {domains.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No domains
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SectionCard>
        </>
      )}

      {tab === "categories" && (
        <>
          <SectionCard title="Add Category">
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Code"
                value={categoryForm.code}
                onChange={(e) =>
                  setCategoryForm((s) => ({ ...s, code: e.target.value }))
                }
                size="small"
              />
              <TextField
                label="Name"
                value={categoryForm.name}
                onChange={(e) =>
                  setCategoryForm((s) => ({ ...s, name: e.target.value }))
                }
                size="small"
                sx={{ minWidth: 260 }}
              />
              <Button variant="contained" onClick={createCategory}>
                Create
              </Button>
            </Box>
          </SectionCard>

          <SectionCard title="Categories">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" width="120">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.id || c.code}>
                    <TableCell>{c.code}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell align="right">
                      <Button
                        color="error"
                        size="small"
                        onClick={() =>
                          remove("categories", c.id, () =>
                            setCategories((prev) =>
                              prev.filter((x) => x.id !== c.id)
                            )
                          )
                        }
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No categories
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SectionCard>
        </>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// small fallback icon
function RefreshIconFallback() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      style={{ display: "block" }}
    >
      <path
        fill="currentColor"
        d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 13.65-6.65Z"
      />
    </svg>
  );
}

/* ========= Upload Progress Dialog (Option B) ========= */
function UploadProgressDialog({ open, flow, stepIndex, statusText }) {
  const FILE_STEPS = [
    "Prepare file(s)",
    "Validate section folder",
    "Upload to S3",
    "Process & create embeddings",
    "Done",
  ];
  const FOLDER_STEPS = [
    "Scan folder structure",
    "Create missing folders in S3",
    "Upload files to S3",
    "Process & create embeddings",
    "Done",
  ];
  const USER_S3_STEPS = [
    "Prepare file",
    "Upload to user S3 folder",
    "Save upload metadata",
    "Done",
  ];
  // ✅ Bare Code steps - PDF to S3, Excel to Bare Code API
  const BARE_CODE_STEPS = [
    "Prepare Bare Code PDF & Excel",
    "Upload Bare Code PDF to S3",
    "Upload Bare Code Excel to Bare Code API",
    "Done",
  ];

  let steps = FILE_STEPS;
  if (flow === "folder") steps = FOLDER_STEPS;
  else if (flow === "userS3") steps = USER_S3_STEPS;
  else if (flow === "bareCode") steps = BARE_CODE_STEPS;

  const activeStep =
    typeof stepIndex === "number"
      ? Math.min(stepIndex, steps.length - 1)
      : 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      hideBackdrop={false}
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>Uploading…</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 1, mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          <Box sx={{ position: "relative", width: 48, height: 48 }}>
            <CircularProgress size={48} />
            <CloudUploadIcon
              sx={{
                position: "absolute",
                top: 6,
                left: 6,
                fontSize: 36,
                color: "#2563eb",
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {statusText || steps[activeStep] || "Working…"}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ========= AI Summary Decision Dialog (for uploads) ========= */
function AISummaryDialog({ open, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Generate AI summary?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Do you want to generate an AI summary for the uploaded file(s)?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>No</Button>
        <Button variant="contained" onClick={() => onConfirm(true)}>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ========= S3 Picker Dialog ========= */
function S3PickerDialog({ open, onClose, onImport, token}) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const userId = useSelector(
    (state) => state?.account?.user_id || state?.account?.org_member?.id
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    safeFetch(`${API_BASE}/docmgmt/list_user_s3/?user_id=${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) =>
        r.ok
          ? r.json()
          : r.text().then((t) => Promise.reject(t || "Failed to list S3 items"))
      )
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [open, token, userId]);

  const toggle = (key) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Select from User Folder (S3)</DialogTitle>
      <DialogContent dividers>
        {loading && <Typography>Loading…</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && items.length === 0 && (
          <Typography color="text.secondary">
            No files found in the user folder.
          </Typography>
        )}

        {!loading && items.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="48"></TableCell>
                <TableCell>Key</TableCell>
                <TableCell align="right" width="140">
                  Size
                </TableCell>
                <TableCell width="220">Modified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow
                  key={it.key}
                  hover
                  onClick={() => toggle(it.key)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(it.key)}
                      onChange={() => toggle(it.key)}
                    />
                  </TableCell>
                  <TableCell>{it.key}</TableCell>
                  <TableCell align="right">
                    {formatSize(it.size || 0)}
                  </TableCell>
                  <TableCell>
                    {it.modified
                      ? new Date(it.modified).toLocaleString()
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selected.size === 0}
          onClick={() => onImport(Array.from(selected))}
        >
          Import {selected.size ? `(${selected.size})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ========= Main Pane ========= */
const DocumentManagementPane = ({ onGotoAISummary }) => {
  const [files, setFiles] = useState([]);
  const [activeNav, setActiveNav] = useState("drive");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadFlow, setUploadFlow] = useState(null); // "file" | "folder" | "userS3" | "bareCode"
  const [uploadStepIndex, setUploadStepIndex] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentNode, setCurrentNode] = useState({
    id: null,
    level: 0,
    name: "ROOT",
  });
  const [breadcrumbs, setBreadcrumbs] = useState([
    { id: null, name: "ROOT", level: 0 },
  ]);
  const token = localStorage.getItem("labour_law_token");
  const headers = { Authorization: `Bearer ${token}` };
  const uploadRef = useRef(null);
  const folderUploadRef = useRef(null);
  const bareCodePdfInputRef = useRef(null); // older hidden input (still kept)
  const [jurisdictions, setJurisdictions] = useState([]);
  const [states, setStates] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [categories, setCategories] = useState([]);
  const [userS3UploadOpen, setUserS3UploadOpen] = useState(false);
  const userS3FileRef = useRef(null);
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [archiveUploadOpen, setArchiveUploadOpen] = useState(false);
  const archiveFileRef = useRef(null);
  const [selectedArchiveDomain, setSelectedArchiveDomain] = useState("");
  const [selectedArchiveYear, setSelectedArchiveYear] = useState("");
  const [archiveDomain, setArchiveDomain] = useState("");
  const [archiveYear, setArchiveYear] = useState([]);

  const llUserIdFromRedux = useSelector(
    (state) =>
      state?.account?.user_id || state?.account?.org_member?.id || ""
  );
  const [llUserId, setLlUserId] = useState(llUserIdFromRedux);

  const [selJurisdiction, setSelJurisdiction] = useState("");
  const [selState, setSelState] = useState("");
  const [selDomain, setSelDomain] = useState("");
  const [selCategory, setSelCategory] = useState("");

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [pendingIsFolder, setPendingIsFolder] = useState(false);

  const [uploadMenuEl, setUploadMenuEl] = useState(null);
  const [s3DialogOpen, setS3DialogOpen] = useState(false);

  // ✅ NEW: Bare Code dialog state + refs
  const [bareCodeDialogOpen, setBareCodeDialogOpen] = useState(false);
  const [bareCodePdfFile, setBareCodePdfFile] = useState(null);
  const [bareCodeExcelFile, setBareCodeExcelFile] = useState(null);
  const bareCodePdfDialogInputRef = useRef(null);
  const bareCodeExcelDialogInputRef = useRef(null);

  const ARCHIVE_YEARS = Array.from({
    length: new Date().getFullYear() - 1949,
  })
    .map((_, i) => String(1950 + i))
    .reverse();

  const [sectionName, setSectionName] = useState("");

  // NEW: Summary action dialog state
  const [summaryActionDialogOpen, setSummaryActionDialogOpen] =
    useState(false);
  const [summaryDocId, setSummaryDocId] = useState(null);
  async function safeJson(res, fallback) {
    try {
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return fallback;
      return await res.json();
    } catch {
      return fallback;
    }
  }

  useEffect(() => {
    let cancelled = false;

    const loadLists = async () => {
      try {
        const [jRes, sRes, dRes, cRes] = await Promise.all([
          safeFetch(`${API_BASE}/docmgmt/list_jurisdictions/`),
          safeFetch(`${API_BASE}/docmgmt/list_states/`),
          safeFetch(`${API_BASE}/docmgmt/list_domains/`),
          safeFetch(`${API_BASE}/docmgmt/list_categories/`),
        ]);

        const jurisdictions = await safeJson(jRes, []);
        const states = await safeJson(sRes, []);
        const domains = await safeJson(dRes, []);
        const categories = await safeJson(cRes, []);

        if (cancelled) return;
        setJurisdictions(Array.isArray(jurisdictions) ? jurisdictions : []);
        setStates(Array.isArray(states) ? states : []);
        setDomains(Array.isArray(domains) ? domains : []);
        setCategories(Array.isArray(categories) ? categories : []);
      } catch (err) {
        if (err.code === "OFFLINE") {
          // show banner/snack; DO NOT crash
          // showSnackbar("You are offline. Connect to internet.", "warning");
        } else if (err.code === "SERVER_UNREACHABLE") {
          // showSnackbar("Server is unreachable.", "error");
        }
        if (cancelled) return;
        // Always set empty arrays so UI doesn't crash
        setJurisdictions([]);
        setStates([]);
        setDomains([]); 
        setCategories([]);
      }
    };

    loadLists();
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadTrash() {
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/list_trash/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load trash");

      const normalized = (Array.isArray(data) ? data : []).map((n) => ({
        id: n.id,
        name: n.name || n.label,
        is_folder: n.is_folder,
        level: n.level ?? n.ord ?? 0,
        path: n.path,
        type: n.is_folder ? "folder" : extToType(n.name || ""),
        modified: n.modified,
        size: n.size,
        treeType: n.treeType,
        trashed: true,
      }));

      setTrashList(normalized);
    } catch (err) {
      console.error("loadTrash failed:", err);
      setTrashList([]);
      showSnackbar(err.message || "Failed to load trash", "error");
    }
  }
  async function loadChildren(nodeId) {
    try{  
      const res = await safeFetch(
        `${API_BASE}/docmgmt/list_nodes/?parent_id=${nodeId || ""}`,
        { headers }
      );
      const data = await res.json();
      const normalized = data.map((n) => ({
        id: n.id,
        name: n.name || n.label,
        is_folder: n.is_folder,
        level: n.level ?? n.ord ?? 0,
        path: n.path,
        type: n.is_folder ? "folder" : extToType(n.name || ""),
        modified: n.modified,
        size: n.size,
        treeType: n.treeType,
        trashed: n.trashed,
      }));
      setFiles(normalized);
    } catch (err) {
      console.error("loadChildren failed:", err);

      // ✅ crash-proof fallback
      setFiles([]);

      // optional: user-friendly message
      // showSnackbar(err.message || "Failed to load folder", "error");
    }
  }

  useEffect(() => {
    if (activeNav === "drive") {
      loadChildren(currentNode.id);
    }
    if (activeNav === "trash") {
      loadTrash();
    }
    if (activeNav === "archive" && selectedDomain && selectedYear) {
      loadArchive(selectedDomain, selectedYear);
    }
  }, [activeNav, currentNode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeNav === "archive") {
      loadArchiveFiles();
    }
  }, [activeNav]); // eslint-disable-line react-hooks/exhaustive-deps

  // generic helpers
  const generate_summary = async (documentId, extraPayload = {}) => {
    const res = await safeFetch(`${API_BASE}/docmgmt/generate_summary/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ document_id: documentId, ...extraPayload }),
    });

    const text = await res.text().catch(() => "");
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      throw new Error(data.error || text || "generate_summary failed");
    }

    return data;
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);

  const createFolder = async (name) => {
    if (!name || !name.trim()) {
      showSnackbar("Folder name required", "error");
      return;
    }

    const form = new FormData();
    form.append("parent_id", currentNode.id || 0);
    form.append("name", name.trim());
    form.append("is_folder", "true");
    form.append("ord", (currentNode.level ?? 0) + 1);

    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/create_folder/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Folder creation failed");

      await loadChildren(currentNode.id);
      setNewFolderDialogOpen(false);
      showSnackbar("✅ Folder created");
    } catch (err) {
      showSnackbar(err.message, "error");
    }
  };

  const triggerUpload = () => uploadRef.current?.click();

  // 1️⃣ Upload from My Computer – with step dialog + counts + embedding message
  const onUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    setUploadFlow("file");
    setUploadStepIndex(0);
    setUploadStatus("Preparing file(s)...");
    setUploading(true);

    try {
      if (!currentNode || currentNode.level < 5) {
        throw new Error("Upload allowed only inside Section folders.");
      }

      const total = selectedFiles.length;
      let uploadedCount = 0;

      setUploadStepIndex(1);
      setUploadStatus(
        `Validating section folder before upload of ${total} file(s)…`
      );

      setUploadStepIndex(2);
      setUploadStatus(`Starting upload of ${total} file(s) to S3…`);

      for (let index = 0; index < selectedFiles.length; index++) {
        const file = selectedFiles[index];

        setUploadStatus(
          `Uploading "${file.name}" to S3 (${index + 1} of ${total})…`
        );

        const form = new FormData();
        form.append("file", file);
        form.append("node_id", currentNode.id);

        const res = await safeFetch(`${API_BASE}/docmgmt/upload_document/`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");

        uploadedCount += 1;
        setUploadStatus(
          `Uploaded ${uploadedCount} of ${total} file(s) to S3. Creating embeddings & indexing…`
        );
      }

      setUploadStepIndex(3);
      setUploadStatus(
        "Creating embeddings and indexing uploaded document(s)…"
      );

      await loadChildren(currentNode.id);

      setUploadStepIndex(4);
      setUploadStatus(
        `Upload completed. ${uploadedCount} of ${total} file(s) uploaded & indexed.`
      );
      showSnackbar("✅ File(s) uploaded to S3 and embeddings created");
    } catch (err) {
      showSnackbar(`❌ Upload failed: ${err.message}`, "error");
    } finally {
      e.target.value = "";
      setTimeout(() => {
        setUploading(false);
        setUploadFlow(null);
        setUploadStepIndex(0);
        setUploadStatus("");
      }, 400);
    }
  };

  async function uploadSingleFile(file) {
    const form = new FormData();
    form.append("file", file);
    form.append("parent_id", currentNode.id || "");

    const res = await safeFetch(`${API_BASE}/docmgmt/upload_document/`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || "Upload failed");
    }
    return data;
  }

  const [summaryContent, setSummaryContent] = useState("");
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);

  const [summarySmallOpen, setSummarySmallOpen] = useState(false);

  // NEW: state for full summary page + blog fields
  const [summaryPageDocId, setSummaryPageDocId] = useState(null);
  const [blogTitle, setBlogTitle] = useState("");
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [blogImagePreview, setBlogImagePreview] = useState(null);

  const handleSummaryImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setBlogImageFile(null);
      setBlogImagePreview(null);
      return;
    }
    setBlogImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setBlogImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // ✅ Blog API kept (not used now for From-Host flow; publish happens in BlogsPane)
  const createBlogPost = async ({
    title,
    tag,
    content,
    documentId,
    imageFile,
  }) => {
    const form = new FormData();
    form.append("title", (title || "").trim() || "From the Host");
    form.append("tag", tag || "From the Host");
    form.append("content", (content || "").trim());

    if (documentId) form.append("document_id", String(documentId));
    if (imageFile) form.append("image", imageFile);

    const res = await safeFetch(`${API_BASE}/usermgmt/blog_post/`, {
      method: "POST",
      body: form,
    });

    const txt = await res.text().catch(() => "");
    let data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      throw new Error(data?.error || txt || "Failed to create blog post");
    }

    return data;
  };

  // ✅ UPDATED: NO PUBLISH HERE. Only redirect/send draft to BlogsPane
  const handlePostFromHostFromSummary = () => {
    console.group("🟦 handlePostFromHostFromSummary");

    console.log("➡️ Clicked: Post To From the Host");
    if (!summaryPageDocId) {
      console.error("❌ No document selected", { summaryPageDocId });
      showSnackbar("No document selected", "error");
      console.groupEnd();
      return;
    }
    if (!summaryContent) {
      console.error("❌ Summary is empty", { summaryContent });
      showSnackbar("Summary is empty", "error");
      console.groupEnd();
      return;
    }

    try {
      const payload = {
        title: blogTitle,
        tag: "From the Host",
        content: summaryContent,
        summary: summaryContent,
        documentId: summaryPageDocId,
        imageFile: blogImageFile,
        imagePreview: blogImagePreview,
        // ✅ Explicitly mark this as a draft-only handoff (BlogsPane should publish from there)
        draftOnly: true,
      };
      console.log("📦 Payload prepared:", payload);
      // ✅ Send draft to BlogsPane
      window.dispatchEvent(
        new CustomEvent("BLOG_DRAFT_FROM_HOST", { detail: payload })
      );
      console.log("📣 BLOG_DRAFT_FROM_HOST event dispatched");

      // ✅ Trigger navigation intent (BlogsPane / parent can listen and switch tab)
      window.dispatchEvent(
        new CustomEvent("NAVIGATE_TO_BLOGS_PANE", {
          detail: { from: "DocumentManagementPane" },
        })
      );
      console.log("🧭 NAVIGATE_TO_BLOGS_PANE event dispatched");

      // ✅ Keep old flow too (not removed) – but pass intent so it can route to BlogsPane if implemented
      if (onGotoAISummary) {
        console.log("🔁 Calling onGotoAISummary callback");
        onGotoAISummary({
          summary: summaryContent,
          documentId: summaryPageDocId,
          title: blogTitle,
          imageFile: blogImageFile,
          imagePreview: blogImagePreview,
          draftOnly: true,
          goTo: "blogs",
        });
      } else {
      console.warn("⚠️ onGotoAISummary callback not provided")
      }

      showSnackbar("✅ Draft sent to Blogs. Publish from BlogsPane.", "success");
      setSummaryDialogOpen(false);

      setBlogTitle("");
      setBlogImageFile(null);
      setBlogImagePreview(null);
      console.log("🧹 UI state reset");
    } catch (e) {
      console.error("❌ Failed to send draft", e);
      showSnackbar(`❌ ${e.message || "Failed to send draft"}`, "error");
    }
    console.groupEnd();
  };
  


  // generate summary & open dialog
  const generateSummary = async (documentId) => {
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/generate_ai_summary/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ document_id: documentId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      showSnackbar("✅ Summary generated successfully", "success");
      setSummaryContent(data.summary || data.summary_text || "No summary found");

      setSummaryDialogOpen(true);
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    }
  };

  const handleSummaryClick = (documentId) => {
    setSummaryDocId(documentId);
    setSummaryActionDialogOpen(true);
  };

  // ✅ create notification draft for a given document (NO orgid logic)
  const notifyForDocument = async (documentId) => {
    try {
      if (!documentId) {
        showSnackbar("Missing document id", "error");
        return;
      }

      // ✅ FIX: use API_BASE (not localhost) and keep payload orgid-free
      const res = await safeFetch(
        `${API_BASE}/notifications/create_for_document/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ document_id: documentId }),
        }
      );

      const text = await res.text().catch(() => "");
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        throw new Error(data.error || text || "Failed to create notification");
      }

      showSnackbar(
        "Notification draft created. Check Notification Centre to send it.",
        "success"
      );
    } catch (err) {
      showSnackbar(
        err.message || "Failed to create notification for document",
        "error"
      );
    }
  };

  const openUploadMenu = (e) => setUploadMenuEl(e.currentTarget);
  const closeUploadMenu = () => setUploadMenuEl(null);
  const chooseMyComputer = () => {
    closeUploadMenu();
    uploadRef.current?.click();
  };
  const chooseS3Folder = () => {
    closeUploadMenu();
    setS3DialogOpen(true);
  };

  // ✅ UPDATED: menu click opens dialog, not direct file input
  const handleBareCodePdfClick = () => {
    closeUploadMenu();
    setBareCodeDialogOpen(true);
  };

  // ✅ NEW: handlers for dialog file selection
  const handleBareCodePdfChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setBareCodePdfFile(file);
  };
  const handleBareCodeExcelChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setBareCodeExcelFile(file);
  };

  // Old handler kept (not used anymore, to avoid removing anything)
  const handleBareCodeDialogUpload = async () => {
    if (!bareCodePdfFile || !bareCodeExcelFile) {
      showSnackbar("Please select both Bare Code PDF and Excel file.", "error");
      return;
    }

    setBareCodeDialogOpen(false);
    setUploadFlow("bareCode");
    setUploading(true);
    setUploadStepIndex(0);
    setUploadStatus("Uploading Bare Act PDF & Excel…");

    try {
      const form = new FormData();
      form.append("pdf_file", bareCodePdfFile);
      form.append("excel_file", bareCodeExcelFile);
      form.append("parent_id", currentNode?.id || "");

      const res = await safeFetch(`${API_BASE}/docmgmt/bare_code_upload/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch {}

      if (!res.ok) {
        throw new Error(data.error || text || "Bare Act upload failed");
      }

      showSnackbar("✅ Bare Act successfully uploaded (PDF + Excel)", "success");

      if (currentNode) await loadChildren(currentNode.id);

      setBareCodePdfFile(null);
      setBareCodeExcelFile(null);
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    } finally {
      setUploading(false);
      setUploadFlow(null);
      setUploadStepIndex(0);
      setUploadStatus("");
    }
  };

  const onChooseFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;
    setPendingUploads(selected);
    setPendingIsFolder(false);
    setAiDialogOpen(true);
  };

  const onChooseFolder = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;
    setPendingUploads(selected);
    setPendingIsFolder(true);
    setAiDialogOpen(true);
  };

  async function importFromS3(keys) {
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/import_from_s3/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ keys, parent_id: currentNode.id || "" }),
      });
      const txt = await res.text().catch(() => "");
      if (!res.ok) throw new Error(txt || "Import failed");
      showSnackbar("✅ Imported from S3");
      await loadChildren(currentNode.id);
      setS3DialogOpen(false);
    } catch (e) {
      showSnackbar(`❌ ${e.message}`, "error");
    }
  }

  function applyNodeToSelections(node) {
    if (!node || !node.level) return;
    switch (node.level) {
      case 1:
        setSelJurisdiction(node.name);
        break;
      case 2:
        setSelState(node.name);
        break;
      case 3:
        setSelDomain(node.name);
        break;
      case 4:
        setSelCategory(node.name);
        break;
      case 5:
        setSectionName(node.name);
        break;
      default:
        break;
    }
  }

  const handleFolderClick = (file) => {
    applyNodeToSelections(file);

    setCurrentNode({
      id: file.id,
      name: file.name,
      level: file.level ?? 0,
    });

    setBreadcrumbs((prev) => [
      ...prev,
      { id: file.id, name: file.name || file.label, level: file.level },
    ]);

    loadChildren(file.id);
  };

  function goBack() {
    if (breadcrumbs.length <= 1) return;
    const newTrail = [...breadcrumbs];
    newTrail.pop();
    const last = newTrail[newTrail.length - 1];

    setBreadcrumbs(newTrail);
    setCurrentNode(last);
    loadChildren(last.id);
  }

  const handleBreadcrumbClick = (index) => {
    if (index === breadcrumbs.length - 1) return;
    const newTrail = breadcrumbs.slice(0, index + 1);
    const last = newTrail[newTrail.length - 1];

    setBreadcrumbs(newTrail);
    setCurrentNode(last);
    loadChildren(last.id);
  };

  // 2️⃣ Upload Folder – with step dialog + counts + embedding message
  const handleFolderUpload = async (e) => {
    const token = localStorage.getItem("labour_law_token");
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadFlow("folder");
    setUploadStepIndex(0);
    setUploadStatus("Scanning folder structure…");
    setUploading(true);

    try {
      const totalFiles = files.length;
      let uploadedCount = 0;

      setUploadStepIndex(1);
      setUploadStatus(
        "Creating / validating nested folders in S3 for this upload…"
      );

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fullPath = file.webkitRelativePath;
        if (!fullPath) {
          throw new Error("Use Chrome for folder upload.");
        }

        const parts = fullPath.split("/");
        const filename = parts.pop();
        let parentId = currentNode.id || 0;

        for (const folderName of parts) {
          const res = await safeFetch(
            `${API_BASE}/docmgmt/list_nodes/?parent_id=${parentId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const existing = await res.json();

          const folder = existing.find(
            (n) => n.name === folderName && n.is_folder
          );

          if (folder) {
            parentId = folder.id;
          } else {
            const formFolder = new FormData();
            formFolder.append("parent_id", parentId);
            formFolder.append("name", folderName);

            const cf = await safeFetch(`${API_BASE}/docmgmt/create_folder/`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: formFolder,
            });

            const created = await cf.json();
            if (!cf.ok) {
              showSnackbar(
                `❌ Folder create failed: ${created.error}`,
                "error"
              );
              throw new Error(created.error || "Folder create failed");
            }
            parentId = created.node_id;
          }
        }

        const form = new FormData();
        form.append("file", file);
        form.append("node_id", parentId);

        setUploadStepIndex(2);
        setUploadStatus(
          `Uploading "${filename}" to S3 (${uploadedCount + 1} of ${totalFiles})…`
        );

        const uf = await safeFetch(`${API_BASE}/docmgmt/upload_document/`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });

        const response = await uf.json();
        if (!uf.ok) {
          throw new Error(response.error || "File upload failed");
        }

        uploadedCount += 1;
        setUploadStatus(
          `Uploaded ${uploadedCount} of ${totalFiles} file(s) to S3. Remaining: ${
            totalFiles - uploadedCount
          }`
        );
      }

      setUploadStepIndex(3);
      setUploadStatus(
        "Processing uploaded documents & creating embeddings for each file…"
      );

      await loadChildren(currentNode.id);

      setUploadStepIndex(4);
      setUploadStatus(
        `Folder upload completed. ${uploadedCount} of ${totalFiles} file(s) uploaded & indexed.`
      );
      showSnackbar(
        `✅ Folder uploaded to S3. ${uploadedCount}/${totalFiles} file(s) processed with embeddings`
      );
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    } finally {
      e.target.value = "";
      setTimeout(() => {
        setUploading(false);
        setUploadFlow(null);
        setUploadStepIndex(0);
        setUploadStatus("");
      }, 400);
    }
  };

  // 3️⃣ User Upload to S3 – with step dialog
  async function handleUserS3Upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFlow("userS3");
    setUploadStepIndex(0);
    setUploadStatus("Preparing file…");
    setUploading(true);

    const form = new FormData();
    form.append("ll_user", llUserId);
    form.append("file", file);

    try {
      setUploadStepIndex(1);
      setUploadStatus("Uploading to user S3 folder…");

      const res = await safeFetch(`${API_BASE}/docmgmt/user_upload_to_s3/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");

      setUploadStepIndex(2);
      setUploadStatus("Saving upload details in database…");

      showSnackbar("✅ File uploaded to user S3 folder");

      setUploadStepIndex(3);
      setUploadStatus("Done");
      e.target.value = "";
      setUserS3UploadOpen(false);
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadFlow(null);
        setUploadStepIndex(0);
        setUploadStatus("");
      }, 400);
    }
  }

  async function handleArchiveUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!archiveDomain || !archiveYear) {
      showSnackbar("Please select domain and year", "error");
      return;
    }

    const form = new FormData();
    form.append("domain", archiveDomain);
    form.append("year", archiveYear);
    form.append("file", file);

    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/archive_upload/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");

      showSnackbar("✅ Archive upload successful");

      e.target.value = "";
      setArchiveUploadOpen(false);

      if (activeNav === "archive") {
        if (selectedArchiveDomain && selectedArchiveYear) {
          await loadArchiveFiles(selectedArchiveDomain, selectedArchiveYear);
        }
      }
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    }
  }

  async function loadArchiveYears(domain) {
    try {
      const res = await safeFetch(
        `${API_BASE}/docmgmt/archive_list_years/?domain=${domain}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setArchiveYear(data);
      setSelectedYear(null);
      setArchiveFiles([]);
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
    }
  }

  async function loadArchiveFiles() {
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/archive_list_files/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed loading archive");

      const rows = data.map((f, idx) => ({
        id: idx + 1,
        name: f.filename,
        path: f.s3_key,
        type: "other",
        is_folder: false,
        size: 0,
        modified: f.uploaded_at,
        domain: f.domain,
        year: f.year,
      }));

      setArchiveFiles(rows);
    } catch (err) {
      showSnackbar(`❌ ${err.message}`, "error");
      setArchiveFiles([]);
    }
  }

  async function loadArchive() {
    const res = await safeFetch(`${API_BASE}/docmgmt/archive_list_files/`, {
      headers,
    });
    const data = await res.json();
    setArchiveFiles(data);
  }

  const handleOpenNewFolderDialog = () => {
    if (!currentNode) {
      setCurrentNode({ id: null, level: 0, name: "ROOT" });
    }
    setNewFolderDialogOpen(true);
  };

  const moveToTrash = async (ids) => {
    if (!ids?.length) return;

    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/move_to_trash/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });

      const text = await res.text().catch(() => "");
      if (!res.ok) {
        throw new Error(text || "Failed to move to Trash");
      }

      await loadChildren(currentNode.id);
      if (activeNav === "trash") {
        await loadTrash();
      }

      showSnackbar("✅ Moved to Trash");
    } catch (err) {
      showSnackbar(
        `❌ ${err.message || "Move to Trash failed"}`,
        "error"
      );
    }
  };

  const restoreFromTrash = async (ids) => {
    if (!ids?.length) return;

    try {
      await safeFetch(`${API_BASE}/docmgmt/restore_from_trash/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });

      await loadChildren(currentNode.id);
      loadTrash();
      showSnackbar("✅ Restored successfully");
    } catch (err) {
      showSnackbar("❌ Restore failed", "error");
    }
  };

  const deleteForever = async (ids) => {
    if (!ids?.length) return;

    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/delete_forever/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });

      const text = await res.text().catch(() => "");
      if (!res.ok) {
        throw new Error(text || "Failed to delete permanently");
      }

      await loadChildren(currentNode.id);
      await loadTrash();
      showSnackbar("✅ Deleted permanently");
    } catch (err) {
      showSnackbar(
        `❌ ${err.message || "Delete permanently failed"}`,
        "error"
      );
    }
  };

  const driveRows = useMemo(
    () =>
      files.filter(
        (f) =>
          !f.trashed &&
          !String(f.path || "").toLowerCase().startsWith("trash")
      ),
    [files]
  );

  const recentCutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return +d;
  }, []);
  const recentRows = useMemo(
    () =>
      files
        .filter(
          (f) =>
            !f.trashed &&
            Date.parse(f.modified || "") >= recentCutoff
        )
        .sort((a, b) => +new Date(b.modified) - +new Date(a.modified)),
    [files, recentCutoff]
  );

  const [trashList, setTrashList] = useState([]);
 
  const trashRows = useMemo(() => trashList, [trashList]);
  const archiveRows = useMemo(() => archiveFiles, [archiveFiles]);

  const NAV_ITEMS = [
    { key: "drive", label: DRIVE_LABEL, icon: <FolderIcon /> },
    { key: "recent", label: "Recent", icon: <AccessTimeIcon /> },
    { key: "trash", label: "Trash", icon: <DeleteIcon /> },
    { key: "manage", label: "Manage Database", icon: <StorageIcon /> },
    { key: "archive", label: "Archive", icon: <ArchiveIcon /> },
  ];

  function NewFolderDialog({
    open,
    onClose,
    currentNode,
    jurisdictions,
    states,
    domains,
    categories,
    onCreateFolder,
    onCreate,
  }) {
    const level = currentNode?.level ?? 0;
    const [name, setName] = useState("");

    const levelConfigs = {
      0: {
        label: "Jurisdiction",
        type: "select",
        data: jurisdictions.map((j) => ({ value: j.scope, label: j.scope })),
      },
      1: {
        label: "State",
        type: "select",
        data: states.map((s) => ({ value: s.name, label: s.name })),
      },
      2: {
        label: "Domain",
        type: "select",
        data: domains.map((d) => ({ value: d.name, label: d.name })),
      },
      3: {
        label: "Category",
        type: "select",
        data: categories.map((c) => ({ value: c.name, label: c.name })),
      },
      4: {
        label: "Section / Sub-Section Name",
        type: "text",
      },
      5: {
        label: "Folder Name",
        type: "text",
      },
    };

    const config = levelConfigs[level] || levelConfigs[5];
    const { label, type, data } = config;

    const handleSubmit = () => {
      if (level === 0) setSelJurisdiction(name);
      if (level === 1) setSelJurisdiction(name);
      if (level === 2) setSelState(name);
      if (level === 3) setSelDomain(name);
      if (level === 4) setSelCategory(name);
      if (level >= 5) setSectionName(name);

      onCreateFolder && onCreateFolder();
    };

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Create Folder</DialogTitle>

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          {type === "select" ? (
            <TextField
              select
              label={label}
              value={name}
              onChange={(e) => setName(e.target.value)}
            >
              {data.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label={label}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              onCreate(name);
              onClose();
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        bgcolor: "linear-gradient(to right, #f3f4f6, #ffffff)",
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: 260,
          minWidth: 260,
          maxWidth: 260,
          flexShrink: 0,
          borderRight: "1px solid #e5e7eb",
          p: 2,
          bgcolor: "#f9fafb",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            pl: 0.5,
          }}
        >
          <DescriptionIcon sx={{ fontSize: "2rem", color: "#2563eb" }} />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}
            >
              Labour Law
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#111827", lineHeight: 1.2 }}
            >
              Legal Drive
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={openUploadMenu}
          sx={{
            textTransform: "none",
            width: "100%",
            mb: 1,
            bgcolor: "#2563eb",
            "&:hover": { bgcolor: "#1d4ed8" },
          }}
          disabled={uploading}
        >
          Upload File
        </Button>
        <Button
          variant="outlined"
          startIcon={<ArchiveIcon />}
          onClick={() => setArchiveUploadOpen(true)}
          sx={{
            textTransform: "none",
            width: "100%",
            mb: 1.5,
          }}
          disabled={uploading}
        >
          Upload to Archive
        </Button>

        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUserS3UploadOpen(true)}
          sx={{ textTransform: "none", width: "100%", mb: 1.5 }}
          disabled={uploading}
        >
          User Upload (S3)
        </Button>

        <Menu
          anchorEl={uploadMenuEl}
          open={Boolean(uploadMenuEl)}
          onClose={closeUploadMenu}
        >
          <MenuItem onClick={chooseMyComputer}>Upload From My Computer</MenuItem>
          <MenuItem onClick={chooseS3Folder}>Import From S3</MenuItem>
          <MenuItem onClick={handleBareCodePdfClick}>
            Upload Bare Code
          </MenuItem>
        </Menu>

        <Button
          variant="outlined"
          startIcon={<DriveFolderUploadIcon />}
          onClick={() => folderUploadRef.current?.click()}
          sx={{ textTransform: "none", width: "100%", mb: 2 }}
          disabled={uploading}
        >
          Upload Folder
        </Button>

        <Divider sx={{ my: 2 }} />

        <List dense>
          {NAV_ITEMS.map((item) => {
            const selected = activeNav === item.key;
            return (
              <ListItemButton
                key={item.key}
                selected={selected}
                onClick={() => !uploading && setActiveNav(item.key)}
                sx={{
                  borderRadius: "0 999px 999px 0",
                  pl: 2.5,
                  pr: 2,
                  py: 1,
                  mb: 0.5,
                  color: selected ? "#1d4ed8" : "#4b5563",
                  "&.Mui-selected": {
                    backgroundColor: "#e0ecff",
                  },
                  "&:hover": { backgroundColor: "#f3f4f6" },
                }}
                disabled={uploading}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: selected ? 600 : 400,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Main */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f9fafb",
        }}
      >
        {/* Header with Back + Path */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
          }}
        >
          {breadcrumbs.length > 1 && activeNav === "drive" && (
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={goBack}
              sx={{ textTransform: "none", color: "#4b5563", mr: 2 }}
              disabled={uploading}
            >
              Back
            </Button>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: "#111827" }}>
              {currentNode.name}
            </Typography>

            {activeNav === "drive" && (
              <Breadcrumbs
                sx={{ mt: 0.5, fontSize: "0.85rem" }}
                separator="›"
                aria-label="breadcrumb"
              >
                {breadcrumbs.map((crumb, index) => (
                  <Typography
                    key={crumb.id ?? `${crumb.name}-${index}`}
                    onClick={() => !uploading && handleBreadcrumbClick(index)}
                    sx={{
                      cursor:
                        index === breadcrumbs.length - 1 || uploading
                          ? "default"
                          : "pointer",
                      textDecoration:
                        index === breadcrumbs.length - 1 || uploading
                          ? "none"
                          : "underline",
                      color:
                        index === breadcrumbs.length - 1
                          ? "text.primary"
                          : "primary.main",
                      whiteSpace: "nowrap",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {crumb.name}
                  </Typography>
                ))}
              </Breadcrumbs>
            )}
          </Box>
        </Box>

        {activeNav === "drive" && (
          <FileTable
            title={currentNode.name}
            rows={driveRows}
            showNewButtons={true}
            onCreateFolder={handleOpenNewFolderDialog}
            onTriggerUpload={() => uploadRef.current?.click()}
            onRowClick={handleFolderClick}
            onMoveToTrash={moveToTrash}
            onGenerateSummary={handleSummaryClick}
            // ✅ Notify handler (no orgid)
            onNotifyDocument={notifyForDocument}
          />
        )}
        {activeNav === "recent" && (
          <FileTable
            title="Recent Legal Documents (last 30 days)"
            rows={recentRows}
            onMoveToTrash={moveToTrash}
            onGenerateSummary={handleSummaryClick}
            // ✅ Notify handler (no orgid)
            onNotifyDocument={notifyForDocument}
          />
        )}
        {activeNav === "trash" && (
          <FileTable
            title="Trash"
            rows={trashRows}
            onRestore={restoreFromTrash}
            onDeleteForever={deleteForever}
          />
        )}
        {activeNav === "manage" && (
          <ManageDatabase showSnackbar={showSnackbar} />
        )}
        {activeNav === "archive" && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Archive
            </Typography>
            <FileTable title="Archive" rows={archiveRows} />
          </Box>
        )}
      </Box>

      <NewFolderDialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
        currentNode={currentNode}
        jurisdictions={jurisdictions}
        states={states}
        domains={domains}
        categories={categories}
        onCreate={createFolder}
      />

      {/* Hidden inputs */}
      <input
        ref={uploadRef}
        type="file"
        multiple
        onChange={onUpload}
        style={{ display: "none" }}
      />
      <input
        ref={folderUploadRef}
        type="file"
        multiple
        webkitdirectory=""
        directory=""
        onChange={handleFolderUpload}
        style={{ display: "none" }}
      />
      {/* Legacy Bare Code input (kept, not triggered anymore) */}
      {/* <input
        ref={bareCodePdfInputRef}
        type="file"
        multiple
        accept=".pdf,application/pdf,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleBareCodeUpload}
        style={{ display: "none" }}
      /> */}

      {/* S3 Picker */}
      <S3PickerDialog
        open={s3DialogOpen}
        onClose={() => setS3DialogOpen(false)}
        onImport={importFromS3}
        token={token}
      />

      {/* User Upload to S3 Dialog */}
      <Dialog
        open={userS3UploadOpen}
        onClose={() => setUserS3UploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload File</DialogTitle>

        <DialogContent dividers>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => userS3FileRef.current?.click()}
            sx={{ textTransform: "none" }}
          >
            Choose File
          </Button>

          <input
            ref={userS3FileRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleUserS3Upload}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setUserS3UploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Archive Upload Dialog */}
      <Dialog
        open={archiveUploadOpen}
        onClose={() => setArchiveUploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Document to Archive</DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            select
            label="Domain"
            value={archiveDomain}
            onChange={(e) => setArchiveDomain(e.target.value)}
            sx={{ mb: 2 }}
          >
            {domains.map((d) => (
              <MenuItem key={d.id} value={d.name}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Year"
            value={archiveYear}
            onChange={(e) => setArchiveYear(e.target.value)}
            sx={{ mb: 2 }}
          >
            {ARCHIVE_YEARS.map((year) => (
              <MenuItem key={year} value={String(year)}>
                {year}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => archiveFileRef.current?.click()}
            sx={{ textTransform: "none" }}
          >
            Choose File
          </Button>

          <input
            type="file"
            ref={archiveFileRef}
            style={{ display: "none" }}
            onChange={handleArchiveUpload}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setArchiveUploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Bare Code Upload Dialog */}
      <Dialog
        open={bareCodeDialogOpen}
        onClose={() => {
          if (uploading) return;
          setBareCodeDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Bare Code</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please select both the Bare Act PDF and the corresponding Excel
            mapping file. Upload will start only when both are selected.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => bareCodePdfDialogInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Select Bare Code PDF
              </Button>
              {bareCodePdfFile && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  Selected: {bareCodePdfFile.name}
                </Typography>
              )}
              <input
                ref={bareCodePdfDialogInputRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={handleBareCodePdfChange}
              />
            </Box>

            <Box>
              <Button
                variant="outlined"
                startIcon={<TableChartIcon />}
                onClick={() => bareCodeExcelDialogInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Select Bare Code Excel
              </Button>
              {bareCodeExcelFile && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  Selected: {bareCodeExcelFile.name}
                </Typography>
              )}
              <input
                ref={bareCodeExcelDialogInputRef}
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={handleBareCodeExcelChange}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (uploading) return;
              setBareCodeDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBareCodeDialogUpload}
            disabled={!bareCodePdfFile || !bareCodeExcelFile || uploading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary PAGE dialog – with From Host redirect only + image upload */}
      <Dialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Summary</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: "#f9fafb",
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.7,
                color: "#111827",
                fontSize: "0.95rem",
                fontFamily:
                  "'Inter','system-ui','-apple-system','BlinkMacSystemFont','Segoe UI',sans-serif",
              }}
            >
              {summaryContent}
            </Typography>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handlePostFromHostFromSummary}>
            Post To From the Host
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary action dialog – Cancel + Show Summary */}
      <Dialog
        open={summaryActionDialogOpen}
        onClose={() => setSummaryActionDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Summary Options</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            What would you like to do with this document?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryActionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              const id = summaryDocId;

              setSummaryActionDialogOpen(false);

              if (id) {
                try {
                  const res = await safeFetch(`${API_BASE}/docmgmt/generate_summary/`, {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ document_id: id }),
                  });

                  const data = await res.json();

                  if (!res.ok) {
                    showSnackbar("Failed to load summary", "error");
                    return;
                  }

                  const summary =
                    data.summary_text ||
                    data.summary ||
                    data.ai_summary ||
                    data.result ||
                    "Summary not available";

                  setSummaryContent(summary);
                  setSummaryPageDocId(id);
                  setBlogTitle("");
                  setBlogImageFile(null);
                  setBlogImagePreview(null);
                  setSummaryDialogOpen(true);
                } catch (err) {
                  showSnackbar("Summary failed", "error");
                }
              }
            }}
          >
            Show Summary
          </Button>
        </DialogActions>
      </Dialog>

      {/* Small Summary Drawer */}
      <Dialog
        open={summarySmallOpen}
        onClose={() => setSummarySmallOpen(false)}
        PaperProps={{
          sx: {
            width: 380,
            right: 0,
            m: 0,
            position: "fixed",
            height: "100vh",
            borderRadius: 0,
            top: 0,
          },
        }}
      >
        <DialogTitle>Summary</DialogTitle>
        <DialogContent dividers sx={{ overflowY: "auto" }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "#f9fafb",
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.7,
                color: "#111827",
                fontFamily:
                  "'Inter','system-ui','-apple-system','BlinkMacSystemFont','Segoe UI',sans-serif",
              }}
            >
              {summaryContent}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSummarySmallOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload progress dialog */}
      <UploadProgressDialog
        open={uploading}
        flow={uploadFlow}
        stepIndex={uploadStepIndex}
        statusText={uploadStatus}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentManagementPane;
